import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { useLocaleContext } from '../context/LocaleContext';

const AppliedCouponsSummaryTable = ({ appliedCoupons }) => {
  const locale = useLocaleContext();
  const isFrench = locale === 'fr-CA';

  // Filter to only show applied coupons
  const filteredCoupons = Array.isArray(appliedCoupons) 
    ? appliedCoupons.filter(coupon => coupon && coupon.isApplied)
    : [];

  // Helper function to get locale-specific value from bilingual string
  const getLocaleValue = (bilingualString) => {
    if (!bilingualString) return '';
    const str = String(bilingualString);
    const arr = str.split(', ');
    let out = [];
    for (let i = 0; i < arr.length; i += 2) {
      if (arr[i] && arr[i + 1]) {
        out.push(isFrench ? arr[i + 1] : arr[i]);
      }
    }
    return out.join(', ');
  };

  const formatCurrency = (amount) => {
    const numAmount = typeof amount === 'number' ? amount : parseFloat(amount);
    if (isNaN(numAmount)) {
      return amount ? `$${amount}` : '$0.00';
    }
    
    const formatted = new Intl.NumberFormat(isFrench ? 'fr-CA' : 'en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 2
    }).format(Math.abs(numAmount));
    
    return numAmount < 0 ? (isFrench ? `-${formatted}` : formatted.replace('$', '-$')) : formatted;
  };

  if (!filteredCoupons || !Array.isArray(filteredCoupons) || filteredCoupons.length === 0) {
    return null;
  }

  return (
    <div style={{ margin: '1rem 0' }}>
      <h4 style={{ 
        marginBottom: '0.75rem', 
        fontSize: '1rem', 
        fontWeight: '600', 
        color: '#2a2c2e' 
      }}>
        {isFrench ? 'Détails du coupon' : 'Coupon Details'}
      </h4>
      <div style={{ 
        border: '1px solid #d9d9d9', 
        borderRadius: '8px', 
        overflow: 'hidden',
        backgroundColor: '#fff'
      }}>
        {/* Table Header */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '1fr 1fr 1fr 1fr', 
          backgroundColor: '#f5f6f7',
          borderBottom: '1px solid #d9d9d9',
          fontWeight: '600',
          fontSize: '14px',
          color: '#2a2c2e'
        }}>
          <div style={{ padding: '12px 16px', borderRight: '1px solid #d9d9d9' }}>
            {isFrench ? 'Code' : 'Code'}
          </div>
          <div style={{ padding: '12px 16px', borderRight: '1px solid #d9d9d9' }}>
            {isFrench ? 'Type' : 'Type'}
          </div>
          <div style={{ padding: '12px 16px', borderRight: '1px solid #d9d9d9' }}>
            {isFrench ? 'Valeur' : 'Value'}
          </div>
          <div style={{ padding: '12px 16px' }}>
            {isFrench ? 'Durée' : 'Duration'}
          </div>
        </div>
        
        {/* Table Rows */}
        {filteredCoupons.map((coupon, couponIndex) => {
          const couponCode = (coupon.coupon && coupon.coupon.couponCode) || coupon.couponCode || '-';
          const couponTypeRaw = (coupon.coupon && coupon.coupon.couponType) || coupon.couponType || '';
          const couponType = getLocaleValue(couponTypeRaw) || '-';
          const dollarValue = (coupon.coupon && coupon.coupon.dollarValue) || coupon.dollarValue || coupon.promotionAmount || 0;
          const durationRaw = (coupon.coupon && coupon.coupon.duration) || coupon.duration;
          const duration = getLocaleValue(durationRaw) || '-';
          
          return (
            <div 
              key={`coupon-${couponCode}-${couponIndex}`}
              style={{ 
                display: 'grid', 
                gridTemplateColumns: '1fr 1fr 1fr 1fr',
                borderBottom: '1px solid #e2e2e2',
                backgroundColor: '#fff'
              }}
            >
              <div style={{ 
                padding: '12px 16px', 
                borderRight: '1px solid #e2e2e2',
                fontSize: '14px',
                color: '#2a2c2e',
                fontWeight: '500'
              }}>
                {couponCode}
              </div>
              
              <div style={{ 
                padding: '12px 16px', 
                borderRight: '1px solid #e2e2e2',
                fontSize: '14px',
                color: '#2a2c2e'
              }}>
                {couponType}
              </div>
              
              <div style={{ 
                padding: '12px 16px', 
                borderRight: '1px solid #e2e2e2',
                fontSize: '14px',
                color: '#4b286d',
                fontWeight: '600'
              }}>
                {formatCurrency(dollarValue)}
              </div>
              
              <div style={{ 
                padding: '12px 16px',
                fontSize: '14px',
                color: '#2a2c2e'
              }}>
                {duration}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

AppliedCouponsSummaryTable.propTypes = {
  appliedCoupons: PropTypes.arrayOf(PropTypes.shape({
    couponCode: PropTypes.string,
    couponType: PropTypes.string,
    dollarValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    duration: PropTypes.string,
    promotionAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    coupon: PropTypes.shape({
      couponCode: PropTypes.string,
      couponType: PropTypes.string,
      dollarValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      duration: PropTypes.string
    })
  }))
};

AppliedCouponsSummaryTable.defaultProps = {
  appliedCoupons: []
};

export default AppliedCouponsSummaryTable;

